import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {apiClient} from '../../../restClient'
import type {IInitiative, IInitiativeOwner, TPersonLike} from '../../../types'
import {useAppStore} from '../../../store/appStore'
import {
  CREATE_INITIATIVE_ENDPOINT,
  CREATE_INITIATIVE_WITH_TASKS_ENDPOINT,
  DELETE_INITIATIVE_ENDPOINT,
  DELETE_RECYCLE_BIN_ENDPOINT,
  GET_INITIATIVE_FILTERS_ENDPOINT,
  GET_INITIATIVE_OWNERS_ENDPOINT,
  GET_INITIATIVES_GRID_ENDPOINT,
  GET_INITIATIVES_TABLE_ENDPOINT,
  GET_RECYCLE_BIN_ENDPOINT,
  RESTORE_INITIATIVE_ENDPOINT,
  UPDATE_INITIATIVE_ENDPOINT,
} from '../../../api/endPoints'
import {PerPageOneHundred} from '../../../constants/perPageConstants'
import {initiativeActivityApi} from '../../../api/initiativeActivityApi'

type TApiError = {response?: {data?: {message?: string}}}
type TApiMessage = {message?: string; id?: string}

interface ITableParams {
  page: number
  statuses?: string[]
  goals?: string[]
  dates?: string[]
  owners?: string[]
}

interface IOwnerInitiativesParams {
  ownerEmailAddress?: string
  offset?: number
  limit: number
  statuses?: string[]
  goals?: string[]
  dates?: string[]
}

export interface IInitiativePayload {
  name?: string
  description?: string
  status?: string
  owner?: string | TPersonLike | null
  people?: string[] | TPersonLike[]
  year?: string
  dueDate?: string
  goals?: string[]
  id?: string
  [key: string]: unknown
}

export interface IInitiativeWithTasksTaskPayload {
  name: string
  description?: string
  responsible?: TPersonLike
  dueDate?: string
  status?: string
  priorityIndex?: number
}

export interface ICreateInitiativeWithTasksPayload {
  initiative: IInitiativePayload
  tasks: IInitiativeWithTasksTaskPayload[]
}

export interface ICreateInitiativeWithTasksResponse {
  initiativeId: string
  message: string
}

export interface IInitiativeFilters {
  statuses?: string[]
  goals?: string[]
  dates?: string[]
}

export interface IInitiativesTableResponse {
  initiatives: IInitiative[]
  total: number
}

export interface IInitiativesOwnersResponse {
  owners: IInitiativeOwner[]
}

export interface IOwnerInitiativesResponse {
  initiatives: IInitiative[]
}

const fetchInitiativesTable = (
  params: ITableParams,
): Promise<IInitiativesTableResponse> => {
  const {page, statuses = [], goals = [], dates = [], owners = []} = params
  return apiClient
    .get(GET_INITIATIVES_TABLE_ENDPOINT, {
      params: {
        limit: PerPageOneHundred,
        offset: (page - 1) * PerPageOneHundred,
        statuses,
        goals,
        dates,
        owners,
      },
    })
    .then(r => r.data)
}

const fetchInitiativesOwners = (): Promise<IInitiativesOwnersResponse> =>
  apiClient.get(GET_INITIATIVE_OWNERS_ENDPOINT).then(r => r.data)

const fetchOwnerInitiatives = (
  params: IOwnerInitiativesParams,
): Promise<IOwnerInitiativesResponse> =>
  apiClient
    .get(GET_INITIATIVES_GRID_ENDPOINT, {
      params: {
        ownerEmailAddress: params.ownerEmailAddress,
        offset: params.offset ?? 0,
        limit: params.limit,
        statuses: params.statuses ?? [],
        goals: params.goals ?? [],
        dates: params.dates ?? [],
      },
    })
    .then(r => r.data)

const fetchDeletedInitiatives = (
  workforceId: string | number,
): Promise<IInitiative[]> =>
  apiClient
    .get(GET_RECYCLE_BIN_ENDPOINT, {headers: {'x-workforce': workforceId}})
    .then(r => r.data)

const fetchInitiativeFilters = (): Promise<IInitiativeFilters> =>
  apiClient.get(GET_INITIATIVE_FILTERS_ENDPOINT).then(r => r.data)

export const initiativesApi = {
  useInitiativesTable: (params: ITableParams) =>
    useQuery({
      queryKey: ['initiatives', 'table', params],
      queryFn: () => fetchInitiativesTable(params),
      enabled: params.page > 0,
    }),

  useInitiativesOwners: () =>
    useQuery({
      queryKey: ['initiatives', 'owners'],
      queryFn: fetchInitiativesOwners,
    }),

  useOwnerInitiatives: (
    email: string,
    offset: number,
    limit: number,
    filters: IInitiativeFilters,
  ) =>
    useQuery({
      queryKey: ['initiatives', 'grid', email, offset, limit, filters],
      queryFn: () =>
        fetchOwnerInitiatives({
          ownerEmailAddress: email,
          offset,
          limit,
          ...filters,
        }),
      enabled: !!email,
    }),

  useDeletedInitiatives: (workforceId: string | number) =>
    useQuery({
      queryKey: ['initiatives', 'recycle', workforceId],
      queryFn: () => fetchDeletedInitiatives(workforceId),
      enabled: !!workforceId,
    }),

  useInitiativeFilters: () =>
    useQuery({
      queryKey: ['initiatives', 'filters'],
      queryFn: fetchInitiativeFilters,
    }),

  useEmptyRecycleBin: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        workforceId,
      }: {
        workforceId: string | number
      }): Promise<TApiMessage> =>
        apiClient
          .delete(DELETE_RECYCLE_BIN_ENDPOINT, {
            headers: {'x-workforce': workforceId},
          })
          .then(r => r.data),
      onSuccess: () =>
        void qc.invalidateQueries({queryKey: ['initiatives', 'recycle']}),
      onError: () =>
        useAppStore.getState().showError('Empty recycle bin failed'),
    })
  },

  useCreateInitiative: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: IInitiativePayload): Promise<IInitiative> =>
        apiClient.post(CREATE_INITIATIVE_ENDPOINT, payload).then(r => r.data),
      onSuccess: () => {
        void qc.invalidateQueries({queryKey: ['initiatives']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userInitiatives']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'topGoals'],
        })
      },
      onError: (err: TApiError) => {
        const msg = err?.response?.data?.message || ''
        if (msg) useAppStore.getState().showError(msg)
      },
    })
  },

  useCreateInitiativeWithTasks: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (
        payload: ICreateInitiativeWithTasksPayload,
      ): Promise<ICreateInitiativeWithTasksResponse> =>
        apiClient
          .post(CREATE_INITIATIVE_WITH_TASKS_ENDPOINT, payload)
          .then(r => r.data),
      onSuccess: () => {
        void qc.invalidateQueries({queryKey: ['initiatives']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userInitiatives']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'topGoals'],
        })
        void qc.invalidateQueries({queryKey: ['tasks']})
        void initiativeActivityApi.refresh(qc)
      },
      onError: (err: TApiError) => {
        const msg = err?.response?.data?.message || ''
        if (msg) useAppStore.getState().showError(msg)
      },
    })
  },

  useUpdateInitiative: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: IInitiativePayload): Promise<IInitiative> =>
        apiClient.patch(UPDATE_INITIATIVE_ENDPOINT, payload).then(r => r.data),
      onSuccess: (data: IInitiative) => {
        void qc.invalidateQueries({queryKey: ['initiatives']})
        void qc.invalidateQueries({queryKey: ['initiative', String(data?.id)]})
        void qc.invalidateQueries({queryKey: ['teamView', 'userInitiatives']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'topGoals'],
        })
        void initiativeActivityApi.refresh(qc)
      },
      onError: (err: TApiError) => {
        const msg = err?.response?.data?.message || ''
        if (msg) useAppStore.getState().showError(msg)
      },
    })
  },

  useMoveToRecycleBin: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        initiativeId,
      }: {
        initiativeId: string
      }): Promise<TApiMessage> =>
        apiClient
          .patch(DELETE_INITIATIVE_ENDPOINT, {id: initiativeId})
          .then(r => r.data),
      onSuccess: (data: TApiMessage) => {
        void qc.invalidateQueries({queryKey: ['initiatives']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userInitiatives']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'topGoals'],
        })
        if (data?.message) useAppStore.getState().showSuccess(data.message)
      },
      onError: (err: TApiError) => {
        const msg = err?.response?.data?.message || ''
        if (msg) useAppStore.getState().showError(msg)
      },
    })
  },

  useRestoreInitiative: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        initiativeId,
        workforceId,
      }: {
        initiativeId: string
        workforceId: string | number
      }): Promise<TApiMessage> =>
        apiClient
          .patch(
            RESTORE_INITIATIVE_ENDPOINT(initiativeId),
            {},
            {headers: {'x-workforce': workforceId}},
          )
          .then(r => r.data),
      onSuccess: (data: TApiMessage) => {
        void qc.invalidateQueries({queryKey: ['initiatives']})
        void qc.invalidateQueries({queryKey: ['initiatives', 'recycle']})
        void qc.invalidateQueries({queryKey: ['teamView', 'userInitiatives']})
        void qc.invalidateQueries({queryKey: ['home', 'upcomingTasks']})
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'statistics'],
        })
        void qc.invalidateQueries({
          queryKey: ['initiativeAnalytics', 'topGoals'],
        })
        if (data?.message) useAppStore.getState().showSuccess(data.message)
      },
      onError: (err: TApiError) => {
        const msg = err?.response?.data?.message || ''
        if (msg) useAppStore.getState().showError(msg)
      },
    })
  },
} as const
